"use client";

import {
	MARKETING_BANNER_ABOVE_NAVBAR_Z_CLASS,
	MARKETING_BANNER_BOTTOM_BORDER_CLASS,
	MARKETING_BANNER_BOTTOM_SHADOW_CLASS,
	MARKETING_BANNER_UNDERLAY_BG_CLASS,
} from "@marketing/shared/lib/marketing-layout-vars";
import { cn } from "@repo/ui/lib/cn";
import type { ReactNode } from "react";

export interface BannerShellProps {
	children: ReactNode;
	className?: string;
	rightSlot?: ReactNode;
}

export function BannerShell({
	children,
	className,
	rightSlot,
}: BannerShellProps) {
	return (
		<div
			className={cn(
				"w-full",
				MARKETING_BANNER_ABOVE_NAVBAR_Z_CLASS,
				MARKETING_BANNER_BOTTOM_SHADOW_CLASS,
				className,
			)}
		>
			<div
				className={cn(
					"relative isolate w-full overflow-hidden bg-transparent",
					MARKETING_BANNER_BOTTOM_BORDER_CLASS,
				)}
			>
				<div
					aria-hidden="true"
					className={cn(
						"absolute inset-0 z-0",
						MARKETING_BANNER_UNDERLAY_BG_CLASS,
					)}
				/>
				<div
					aria-hidden="true"
					className="pointer-events-none absolute top-1/2 left-[max(-7rem,calc(50%-52rem))] z-[1] -translate-y-1/2 transform-gpu blur-2xl"
				>
					<div
						className="aspect-[577/310] w-[36.0625rem] bg-linear-to-r from-[#ff80b5] to-[#9089fc] opacity-30 dark:opacity-40"
						style={{
							clipPath:
								"polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
						}}
					/>
				</div>
				<div
					aria-hidden="true"
					className="absolute top-1/2 left-[max(45rem,calc(50%+8rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
				>
					<div
						className="aspect-[577/310] w-[36.0625rem] bg-linear-to-r from-[#ff80b5] to-[#9089fc] opacity-30 dark:opacity-40"
						style={{
							clipPath:
								"polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
						}}
					/>
				</div>

				<div className="relative z-10 w-full p-2">
					<div
						className={cn(
							"grid w-full max-w-full items-center",
							rightSlot != null
								? "grid-cols-[minmax(0,1fr)_auto] gap-x-2 min-[380px]:grid-cols-[1fr_auto_1fr] min-[380px]:gap-x-4"
								: "grid-cols-[1fr_auto_1fr] gap-x-4 sm:gap-x-5 lg:gap-x-6",
						)}
					>
						{rightSlot != null ? (
							<div
								className="hidden min-w-0 min-[380px]:block"
								aria-hidden
							/>
						) : (
							<div className="min-w-0" aria-hidden />
						)}
						<div
							className={cn(
								"flex min-w-0 max-w-full items-center",
								rightSlot != null
									? "justify-start text-left min-[380px]:justify-center min-[380px]:justify-self-center min-[380px]:text-center"
									: "justify-center justify-self-center text-center",
								rightSlot != null &&
									"min-[380px]:max-w-[calc(100vw-6rem)] sm:max-w-[calc(100vw-8.5rem)] lg:max-w-[calc(100vw-9rem)]",
							)}
						>
							{children}
						</div>
						<div className="relative z-10 flex min-w-0 shrink-0 items-center justify-self-end">
							{rightSlot}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
